import { map } from 'rxjs/operators';
import { CommonModule } from "@angular/common";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { ApiService } from "app/services/api.service";
import { Observable, Subscription, forkJoin } from "rxjs";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  OnChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { ICustomerInfo } from '../new-staff.component';
import { FormsModule } from '@angular/forms';

interface IAddedCompany {
  company: string;
  role: string;
  staffId: string;
}

@Component({
    selector: "app-new-staff-edit",
    templateUrl: "./new-staff-edit.component.html",
    styleUrls: ["./new-staff-edit.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatDividerModule,
        TranslateModule,
    ]
})
export class NewStaffEditComponent implements OnChanges {
  constructor(
    private api: ApiService,
    private dialog: MatDialog,
    private translate: TranslateService
  ) { }
  @Input() staff: any;
  @Input() customers: ICustomerInfo[];
  @Input() isCreateMode: boolean = false;
  @Input() mode: 'federated' | 'non-federated' = 'federated';

  @Output() onAddCompany = new EventEmitter<void>();
  @Output() onRemoveCompany = new EventEmitter<number>();
  @Output() onMovingSelected = new EventEmitter<any>();
  @Output() onDeleteFailed = new EventEmitter<void>();
  @Output() onCloseEditCard = new EventEmitter<void>();

  dataLoaded: boolean = false;
  deleteConfirmation = false;
  deleteError = false;
  identityUsers: any[] = [];
  hasStrongAuth: boolean = false;
  // Converting a non-federated user into a federated one is hidden for now, but will be
  // taken back into use later. Set to true to bring back the UI and its related requests.
  showFederationConversion: boolean = false;
  usernameInUse = false;

  roles = [
    { value: 101, name: this.translate.instant("STAFF_CUSTOMER_ADMIN") },
    { value: 100, name: this.translate.instant("STAFF_MAIN_USER") },
    { value: 50, name: this.translate.instant("STAFF_USER") },
    { value: 10, name: this.translate.instant("EveronMobileApp") },
    { value: 0, name: this.translate.instant("STAFF_READONLY") },
  ];

  isCompanyAdded(companyId: string) {
    return this.staff.addedCompanies.some((item: IAddedCompany) => item.company === companyId);
  }

  checkUsernameAvailability(): void {
    if (this.staff.username === this.staff.originalUsername) {
      this.usernameInUse = false;
      return;
    }

    if (!this.staff.username) {
      this.usernameInUse = false;
      return;
    }

    this.api.checkUsername(this.staff.username).subscribe({
      next: (res: any) => {
        this.usernameInUse = !res;
      },
      error: () => {
        this.usernameInUse = false;
      }
    });
  }

  startAddingCompany() {
    this.staff.locationAmount += 1;
    this.onAddCompany.emit();
  }

  startRemovingCompany(r_id: number) {
    this.staff.locationAmount -= 1;
    this.onRemoveCompany.emit(r_id);
  }

  private createLandingPageUser() {
    const req = {
      firstName: this.staff.firstName,
      familyName: this.staff.familyName,
      email: this.staff.email,
      username: this.staff.username,
      password: this.staff.password,
      convertToFederated: this.staff.convertToFederated,
      staffRole: this.staff.staffRole
    };
  
    this.api.createLandingPageUser(req).subscribe({
      next: (res: any) => {
  
        const identityId = res.identityId;
  
        const locations = this.staff.addedCompanies
        .filter((x: any) => x.company && x.role)
        .map(x => ({
          identityId,
          companyId: x.company,
          staffRole: x.role,
          firstName: this.staff.firstName,
          familyName: this.staff.familyName,
          email: this.staff.email
        }));

        if (locations.length === 0) {
          this.onCloseEditCard.emit();
          return;
        }
  
        this.api.setStaffCompanyLocation(locations).subscribe({
          next: () => {
            this.onCloseEditCard.emit();
          },
          error: err => console.error(err)
        });
  
      },
      error: err => {
        console.error(err);
      }
    });
  }

  private updateLandingPageUser() {
    let req: any;
  
    try {
      req = this.buildUpdateRequest();
    } catch (e) {
      this.staff.updateFailed = true;
      return;
    }
  
    this.api.updateLandingPageUser(req).subscribe({
      next: () => {
  
        const locations = this.staff.addedCompanies
        .filter((x: any) => x.company && x.role)
        .map(x => ({
          identityId: this.staff.identityId,
          companyId: x.company,
          staffRole: x.role,
          firstName: this.staff.firstName,
          familyName: this.staff.familyName,
          email: this.staff.email
        }));

        const finishUp = () => {
          if (this.showFederationConversion && this.staff.identityUsername && this.staff.staffId) {
            const data = {
              username: this.staff.identityUsername,
              personnelId: this.staff.staffId
            };
            this.api.updateAuthenticatedUser(data, '').subscribe({
              next: () => this.onCloseEditCard.emit(),
              error: err => {
                console.error(err);
                this.staff.updateFailed = true;
              }
            });
          } else {
            this.onCloseEditCard.emit();
          }
        };

        if (locations.length === 0) {
          finishUp();
          return;
        }

        this.api.setStaffCompanyLocation(locations).subscribe({
          next: finishUp,
          error: err => console.error(err)
        });

      },
      error: err => {
        console.error(err);
        this.staff.updateFailed = true;
      }
    });
  }

  private buildUpdateRequest() {
    const s = this.staff;
  
    const req: any = {
      identityId: s.identityId,
      firstName: s.firstName,
      familyName: s.familyName,
      email: s.email,
      username: s.username,
      staffRole: s.staffRole,
    };
  
    const password = (s.password ?? '').trim();
    const confirm = (s.confirmPassword ?? '').trim();
  
    if (password) {
      if (password !== confirm) {
        throw new Error("Passwords do not match");
      }
      req.password = password;
      req.confirmPassword = confirm;
    }
  
    return req;
  }

  deleteLandingPageUser(): void {

    this.deleteError = false;
  
    this.api.deleteLandingPageUser(this.staff.identityId).subscribe({
      next: () => {
        this.onCloseEditCard.emit();
      },
      error: (err) => {
        console.error(err);
        this.deleteError = true;
      }
    });
  }

  startMovingSelected() {
    if (this.mode === 'non-federated') {
      const hasCompanyWithoutRole = this.staff.addedCompanies?.some(
        (c: any) => c.company && (c.role === undefined || c.role === null || c.role === '')
      );
  
      if (hasCompanyWithoutRole) {
        this.staff.roleValidationFailed = true;
        return;
      }
      this.staff.roleValidationFailed = false;
    }
  
    // create non-federated user
    if (this.isCreateMode && this.mode === 'non-federated') {
      this.createLandingPageUser();
      return;
    }
  
    // update non-federated user
    if (!this.isCreateMode && this.mode === 'non-federated') {
      this.updateLandingPageUser();
      return;
    }
    this.onMovingSelected.emit(this.staff);
  }

  initClosingEditCard() {
    this.onCloseEditCard.emit();
  }

  parseAddedCompanies(staffLocations?: any[]): any[] {
    const addedCompanies = [];
    for (const location of staffLocations) {
      addedCompanies.push({
        company: location.companyId,
        role: location.staffRole.toString(),
        staffId: location.staffId,
      });
    }
    return addedCompanies;
  }

  ngOnChanges(): void {
    this.staff.originalUsername = this.staff.username;
    const identityId = this.staff.identityId;
  
    this.api.getStaffCompanyLocations(identityId).subscribe(
      (locations: any) => {
        this.staff.staffLocations = locations;
        this.staff["addedCompanies"] = this.parseAddedCompanies(locations);

        this.dataLoaded = true;
      },
      (error) => console.log(error)
    );
  
    const features: string[] = JSON.parse(localStorage.getItem("features") || "[]");
    this.hasStrongAuth = features.includes("StrongAuthentication");
  
    if (!this.isCreateMode && this.showFederationConversion) {
      this.api.getIdentityList().subscribe((res: any[]) => {
        this.identityUsers = res;
      });
    }
  }

  openDialog(user: any, companyId: any): void {
    // Get locations staff id and company information
    const staffId = user.staffLocations?.find(
      (location: any) => location.companyId === companyId
    )?.staffId;
    if (!staffId) return;

    // The company may no longer be in the company group, so fall back to its ID
    const company = this.customers?.find(
      (customer) => customer.companyId === companyId
    );
    const companyName = company ? `${company.company} (${company.companyId})` : companyId;

    const dialogRef = this.dialog.open(RemoveStaffFromCompanyDialog, {
      width: "450px",
      // Data for dialog object to use
      data: {
        personnel: user.firstName + " " + user.familyName,
        company: companyName,
        staffId: staffId,
      },
    });

    dialogRef.afterClosed().subscribe(
      (removeCompanyResult: Observable<any>) => {
        if (removeCompanyResult) {
          removeCompanyResult.subscribe((result) => {
            if (result) {
              // Remove deleted company from the list...
              const index = user.addedCompanies.findIndex(
                (c: any) => c.company === companyId
              );
              if (index === -1) return;
              const removedLocation = user.addedCompanies.splice(index, 1)[0];

              // ... and from location list to display current location status in the table
              this.staff.locationList = (this.staff.locationList ?? []).filter(
                (x: string) => x !== this.getLocationName(removedLocation.company)
              );
            }
          });
        }
      },
      (err) => {
        this.onDeleteFailed.emit();
      }
    );
  }

  getLocationName(companyId: string): string {
    return this.customers?.find(c => c.companyId === companyId)?.company ?? "";
  }

  updateStaffRole(templateRef: MatSelect, staff: any) {
    if (this.isCreateMode || this.mode === 'non-federated') {
      staff.staffRole = templateRef.value;
      return;
    }

    const identityId = staff.identityId;
    const currentRole = staff.staffRole;
    const selectedRole = templateRef.value;

    this.api.updateNewStaffRole(selectedRole, identityId).subscribe(
      (success: boolean) => {
        if (success) {
          staff.staffRole = selectedRole;
          staff["updateFailed"] = false;
        } else {
          // If updating staff role failed, set dropdown's selected value back to staff's current role
          templateRef.value = currentRole;
          staff["updateFailed"] = true;
        }
      },
      (error) => {
        templateRef.value = currentRole;
        staff["updateFailed"] = true;
      }
    );
  }
}

@Component({
    selector: "remove-company-dialog",
    templateUrl: ".././remove-company-dialog.html",
    styles: [
        `
      h3,
      p {
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
          "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji",
          "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;
      }
      .warning {
        padding: 20px 24px 20px;
        color: #e8413b;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [TranslateModule, MatDialogModule]
})
export class RemoveStaffFromCompanyDialog {
  constructor(
    private api: ApiService,
    private dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  deleteCompany(id: any): Observable<any> {
    return this.api.deleteStaffCompanyLocation(id);
  }

  onNoClick(): void {
    this.dialogRef.close();
  }
}
